/** @jsxImportSource @emotion/react */

import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import PageTemplate from '@page/iyagigo/page-template';
import StudyPlan from '@component/iyagigo/study-plan';
import HeaderProgress from '@component/iyagigo/common/header-progress';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as cacheActions } from '@store/slice/cache-slice';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useStudentKey } from '@hook/iyagigo/useInit';
import { useStudentKeyQuery } from '@hook/iyagigo/useApi';
import { IYAGIGO_STUDY_PLAN_STEPS } from '@constant/iyagigo/variable';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { isStudyPlanDone } from '@component/iyagigo/common/study-page-guard';

const StudyPlanPage: React.FC = () => {
  const dispatch = useTypedDispatch();
  const { studentKey, studentKeySyncing } = useStudentKey();
  const hasStudentKey = Boolean(studentKey);
  const studentQuery = useStudentKeyQuery({ enabled: hasStudentKey });
  const studyConfiguration = studentQuery.data?.data?.studyConfiguration;
  const hasSourceChannel = Boolean(studyConfiguration?.sourceChannel);
  const studyPlanSteps = hasSourceChannel
    ? IYAGIGO_STUDY_PLAN_STEPS.filter((item) => item !== 'source-channel')
    : IYAGIGO_STUDY_PLAN_STEPS;
  const progressStartStep = hasSourceChannel ? 'study-motivation' : 'source-channel';
  const progressStartIndex = studyPlanSteps.indexOf(progressStartStep);
  const step = useTypedSelector((state) => state.rootReducer?.cacheReducer?.studyPlanStep) ?? 'account-choice';
  const flowStepIndex = studyPlanSteps.indexOf(step);
  const stepIndex = Math.max(flowStepIndex - progressStartIndex, 0);
  const totalSteps = studyPlanSteps.length - progressStartIndex;
  const isFirstStep = flowStepIndex === 0;

  useEffect(() => {
    dispatch(cacheActions.storeStudyPlanStep(hasStudentKey ? 'introduction' : 'account-choice'));
  }, [dispatch, hasStudentKey]);

  if (studentKeySyncing) return null;

  if (hasStudentKey && !studentQuery.isPending && !studentQuery.isFetching && isStudyPlanDone(studyConfiguration)) {
    return <Navigate replace to={IYAGIGO_PATH.INITIAL_SERVICE_PATH} />;
  }

  const handleBackClick = () => {
    if (isFirstStep) {
      dispatch(
        modalActions.showDialogModal({
          type: 'plain-text',
          data: '나에게 맞는 학습을 시작하려면\n간단한 질문에 답해 주세요.',
        }),
      );
      return;
    }

    dispatch(cacheActions.storeStudyPlanStep(studyPlanSteps[Math.max(0, flowStepIndex - 1)]));
  };

  return (
    <PageTemplate
      title='학습 준비'
      mode={'back'}
      hideSignin
      handleNavigationClick={handleBackClick}
      customHeader={
        step === 'account-choice' ? (
          'none'
        ) : step === 'introduction' ? undefined : (
          <HeaderProgress currentProgress={stepIndex} totalProgress={totalSteps} />
        )
      }>
      <StudyPlan
        existingSourceChannel={studyConfiguration?.sourceChannel}
        existingStudyLanguage={studyConfiguration?.studyLanguage}
      />
    </PageTemplate>
  );
};

export default StudyPlanPage;
